// Pure helpers of the Rule tab (no API, no React): the editor's form values, the rule and ladder payloads, and the
// one-line readings of a schedule / offset.
import { DAYS_OF_WEEK, MONTHS, OFFSET_UNIT_LABEL, ROLE_LABEL } from "./rule.constants";

let nextKey = 0;
/** A client-side key for a step card (React list key; steps reorder before they have an id). */
const stepKey = () => `step_${++nextKey}`;
const text = (value) => (value === null || value === undefined ? "" : String(value));
const intOrNull = (value) => (text(value).trim() === "" ? null : Number(value));
/** A date-only value from the API (UTC midnight) as "YYYY-MM-DD" for a date input. */
const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");

export const newStep = (index) => ({
  key: stepKey(),
  id: null,
  name: index === 0 ? "First reminder" : `Escalation ${index}`,
  wait_value: index === 0 ? "0" : "2",
  wait_unit: "DAY",
  recipients: [{ type_key: index === 0 ? "SUBJECT_USER" : "ASSIGNED_ADMIN", config: {}, channel_keys: [] }],
  cc_recipients: [],
  channels: [{ channel_key: "EMAIL", template_fid: null, config: {} }],
  digest: false,
  keep_previous: index > 0,
  max_sends: "",
});

const entryToForm = (entry) => ({ type_key: entry.type_key ?? "", config: entry.config ?? {}, channel_keys: entry.channel_keys ?? [] });

const stepToForm = (step) => ({
  key: stepKey(),
  id: step.id,
  name: step.name ?? "",
  wait_value: text(step.wait_value ?? 0),
  wait_unit: step.wait_unit ?? "DAY",
  recipients: (step.recipients ?? []).map(entryToForm),
  cc_recipients: (step.cc_recipients ?? []).map(entryToForm),
  channels: (step.channels ?? []).map((c) => ({ channel_key: c.channel_key, template_fid: c.template_fid ?? null, config: c.config ?? {} })),
  digest: step.digest === true,
  keep_previous: step.keep_previous === true,
  max_sends: text(step.max_sends),
});

/** Editor values of a saved rule (with its steps), or of a new one. Numbers stay text while edited. */
export function toFormValues(record) {
  const s = record?.schedule ?? {};
  const audience = record?.audience ?? {};
  const calendar = record?.calendar_policy ?? {};
  const repeat = record?.repeat_policy ?? {};
  const stop = record?.stop_policy ?? {};
  return {
    code: record?.code ?? "",
    name: record?.name ?? "",
    description: record?.description ?? "",
    trigger_fid: record?.trigger_fid ?? null,
    priority: text(record?.priority ?? 0),
    param_values: Object.fromEntries(Object.entries(record?.param_values ?? {}).map(([key, value]) => [key, typeof value === "boolean" ? (value ? "Yes" : "No") : text(value)])),
    schedule: {
      freq: s.freq ?? "DAILY",
      time: s.time ?? "09:00",
      days_of_week: s.days_of_week ?? [],
      every: text(s.every ?? 1),
      between_on: Array.isArray(s.between),
      between: s.between ?? ["09:00", "18:00"],
      day_of_month: text(s.day_of_month ?? 1),
      month: text(s.month ?? 1),
      cron: s.cron ?? "",
    },
    timezone: record?.timezone ?? "",
    timezone_basis: record?.timezone_basis ?? "RULE",
    offsets: (record?.offsets?.length ? record.offsets : [0]).map(String),
    offset_unit: record?.offset_unit ?? "DAY",
    audience: {
      roles: audience.roles ?? [],
      company_fids: audience.company_fids ?? [],
      include_user_ids: audience.include_user_ids ?? [],
      exclude_user_ids: audience.exclude_user_ids ?? [],
      active_only: audience.active_only !== false,
    },
    calendar_policy: {
      working_days_only: calendar.working_days_only === true,
      quiet_on: Array.isArray(calendar.quiet_hours),
      quiet_hours: calendar.quiet_hours ?? ["21:00", "08:00"],
      on_skip: calendar.on_skip ?? "NEXT_WORKING_DAY",
    },
    repeat_policy: repeat.mode === "EVERY" ? { mode: "EVERY", every: text(repeat.every), unit: repeat.unit, max_count: text(repeat.max_count) } : { mode: "ONCE", every: "1", unit: "DAY", max_count: "3" },
    stop_policy: {
      on_condition_cleared: stop.on_condition_cleared !== false,
      on_subject_inactive: stop.on_subject_inactive !== false,
      on_max_reached: stop.on_max_reached !== false,
      after_days: text(stop.after_days),
    },
    allow_opt_out: record ? record.allow_opt_out !== false : true,
    preference_key: record?.preference_key ?? "",
    valid_from: dateOnly(record?.valid_from),
    valid_to: dateOnly(record?.valid_to),
    tags: record?.tags ?? [],
    is_activated: record ? record.is_activated !== false : true,
    steps: record ? (record.steps ?? []).map(stepToForm) : [newStep(0)],
  };
}

/** Only the keys the chosen frequency reads. */
export function toSchedule(s) {
  const days = s.days_of_week.length ? { days_of_week: [...s.days_of_week].sort((a, b) => a - b) } : {};
  switch (s.freq) {
    case "HOURLY":
      return { freq: "HOURLY", every: Number(s.every), ...(s.between_on && { between: s.between }), ...days };
    case "DAILY":
    case "WEEKLY":
      return { freq: s.freq, time: s.time, ...days };
    case "MONTHLY":
      return { freq: "MONTHLY", time: s.time, day_of_month: Number(s.day_of_month) };
    case "YEARLY":
      return { freq: "YEARLY", time: s.time, month: Number(s.month), day_of_month: Number(s.day_of_month) };
    default:
      return { freq: "CRON", cron: s.cron.trim() };
  }
}

/** Typed parameter values from the inputs; empty ones are left to the trigger's defaults. */
export function toParamValues(values, params) {
  const out = {};
  for (const param of params ?? []) {
    const raw = values?.[param.key];
    if (raw === undefined || raw === "") continue;
    if (param.data_type === "INT" || param.data_type === "NUMERIC") out[param.key] = Number(raw);
    else if (param.data_type === "BOOLEAN") out[param.key] = raw === "Yes";
    else out[param.key] = raw;
  }
  return Object.keys(out).length ? out : null;
}

/** The rule payload. Offsets go only to a date anchor rule, whose repeat is always once. */
export function toRulePayload(values, { params, triggerType }) {
  const isDateAnchor = triggerType === "DATE_ANCHOR";
  const a = values.audience;
  const c = values.calendar_policy;
  const r = values.repeat_policy;
  const s = values.stop_policy;
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    description: values.description.trim() || null,
    trigger_fid: values.trigger_fid,
    priority: Number(values.priority || 0),
    param_values: toParamValues(values.param_values, params),
    schedule: toSchedule(values.schedule),
    timezone: values.timezone || null,
    timezone_basis: values.timezone_basis,
    offsets: isDateAnchor ? values.offsets.map(Number) : [],
    offset_unit: isDateAnchor ? values.offset_unit : "DAY",
    audience: {
      ...(a.roles.length && { roles: a.roles }),
      ...(a.company_fids.length && { company_fids: a.company_fids }),
      ...(a.include_user_ids.length && { include_user_ids: a.include_user_ids }),
      ...(a.exclude_user_ids.length && { exclude_user_ids: a.exclude_user_ids }),
      active_only: a.active_only,
    },
    calendar_policy: { working_days_only: c.working_days_only, ...(c.quiet_on && { quiet_hours: c.quiet_hours }), on_skip: c.on_skip },
    repeat_policy: !isDateAnchor && r.mode === "EVERY" ? { mode: "EVERY", every: Number(r.every), unit: r.unit, max_count: Number(r.max_count) } : { mode: "ONCE" },
    stop_policy: {
      on_condition_cleared: s.on_condition_cleared,
      on_subject_inactive: s.on_subject_inactive,
      on_max_reached: s.on_max_reached,
      ...(s.after_days !== "" && { after_days: Number(s.after_days) }),
    },
    allow_opt_out: values.allow_opt_out,
    preference_key: values.allow_opt_out ? values.preference_key || null : null,
    valid_from: values.valid_from || null,
    valid_to: values.valid_to || null,
    tags: values.tags,
    is_activated: values.is_activated,
  };
}

const entryPayload = (entry, withChannels) => ({
  type_key: entry.type_key,
  config: entry.config ?? {},
  ...(withChannels && entry.channel_keys?.length && { channel_keys: entry.channel_keys }),
});

/** The save-ladder payload: steps in order (step_no is the position); CC only goes with an email channel. */
export const toLadderPayload = (steps) =>
  steps.map((step, index) => {
    const hasEmail = step.channels.some((c) => c.channel_key === "EMAIL");
    return {
      ...(step.id && { id: step.id }),
      name: step.name.trim() || null,
      wait_value: Number(step.wait_value || 0),
      wait_unit: step.wait_unit,
      recipients: step.recipients.map((entry) => entryPayload(entry, true)),
      cc_recipients: hasEmail && step.cc_recipients.length ? step.cc_recipients.map((entry) => entryPayload(entry, false)) : null,
      channels: step.channels.map((c) => ({ channel_key: c.channel_key, template_fid: c.template_fid, config: c.config ?? {} })),
      digest: step.digest,
      keep_previous: index === 0 ? false : step.keep_previous,
      max_sends: intOrNull(step.max_sends),
    };
  });

/** "3 days before", "on the day", "2 hours after". */
export function offsetText(offset, unit = "DAY") {
  const n = Number(offset);
  if (n === 0) return "on the day";
  const label = (OFFSET_UNIT_LABEL[unit] ?? "Days").toLowerCase();
  const amount = Math.abs(n);
  return `${amount} ${amount === 1 ? label.replace(/s$/, "") : label} ${n < 0 ? "before" : "after"}`;
}

/** Every IANA zone the browser knows (the common ones when it can't list them). */
export function timezoneOptions() {
  const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Asia/Kolkata"];
  return (zones.includes("UTC") ? zones : ["UTC", ...zones]).map((zone) => ({ value: zone, label: zone }));
}

/** "Daily at 09:00 (Mon, Tue)", "Every 2 h between 09:00-18:00", "Monthly on the last day at 10:00". */
export function scheduleText(schedule) {
  if (!schedule?.freq) return "—";
  const days = schedule.days_of_week?.length ? ` (${schedule.days_of_week.map((d) => DAYS_OF_WEEK.find((x) => x.value === Number(d))?.label ?? d).join(", ")})` : "";
  const dom = Number(schedule.day_of_month) === -1 ? "the last day" : `day ${schedule.day_of_month ?? "?"}`;
  switch (schedule.freq) {
    case "HOURLY":
      return `Every ${schedule.every ?? 1} h${schedule.between ? ` between ${schedule.between[0]}-${schedule.between[1]}` : ""}${days}`;
    case "DAILY":
      return `Daily at ${schedule.time ?? "?"}${days}`;
    case "WEEKLY":
      return `Weekly at ${schedule.time ?? "?"}${days}`;
    case "MONTHLY":
      return `Monthly on ${dom} at ${schedule.time ?? "?"}`;
    case "YEARLY":
      return `Yearly on ${dom} of ${MONTHS.find((m) => m.value === String(schedule.month))?.label ?? "?"} at ${schedule.time ?? "?"}`;
    default:
      return `Cron ${schedule.cron ?? ""}`;
  }
}

/** "Clients of 2 companies, 3 excluded" — a short reading of an audience ("Everyone" when it filters nothing). */
export function audienceText(audience, { isBroadcast = false } = {}) {
  const a = audience ?? {};
  const parts = [];
  if (a.include_user_ids?.length) parts.push(`${a.include_user_ids.length} picked user${a.include_user_ids.length === 1 ? "" : "s"}`);
  if (a.roles?.length) parts.push(a.roles.map((role) => ROLE_LABEL[role] ?? role).join(" / "));
  if (a.company_fids?.length) parts.push(`${a.company_fids.length} compan${a.company_fids.length === 1 ? "y" : "ies"}`);
  if (a.exclude_user_ids?.length) parts.push(`${a.exclude_user_ids.length} excluded`);
  if (!parts.length) return isBroadcast ? "Every active user" : "Everyone the trigger finds";
  return parts.join(" · ");
}

/** The problems a failed request carries (data.problems), else its message. */
export const problemsOf = (error, fallback) => {
  const list = error?.response?.data?.data?.problems;
  return Array.isArray(list) && list.length ? list : [error?.response?.data?.message || fallback];
};
